import type { CourseWithProgress, UserCourse } from '@teachur/http-client';
import type { CourseStep } from '../contexts/SingleCourseProvider';
import type { SelfAssessSummary } from './selfAssessHelpers';
import type { ExamSummary } from './examHelpers';
import type { ProjectSummary } from './projectHelpers';
import type { CourseHearingSummary } from './hearingHelpers';

export const getCurrentCourseStep = (course: CourseWithProgress): CourseStep | null => {
  if (course.status === 'notStarted') return null;

  if (course.selfAssessmentCompletionPercentage < 100) {
    return 'self';
  }

  if (!course.quizCompleted) {
    return 'exam';
  }

  if (!course.projectCompleted) {
    return 'project';
  }

  return 'hearing';
};

interface GetCurrentStudentCourseStepParams {
  userCourse: UserCourse;
  selfAssessSummary: SelfAssessSummary;
  examSummary: ExamSummary;
  projectSummary: ProjectSummary;
  hearingSummary: CourseHearingSummary;
}

const isSelfAssessCompleted = (summary: SelfAssessSummary) => {
  return (
    summary.totalObjectivesCount > 0 &&
    summary.completedObjectivesCount >= summary.totalObjectivesCount
  );
};

const isExamCompleted = (summary: ExamSummary) => {
  return (
    summary.summaryTotalQuestionsCount > 0 &&
    summary.summaryCorrectAnswersCount >= summary.summaryTotalQuestionsCount
  );
};

const isProjectCompleted = (summary: ProjectSummary) => {
  return summary.status.toLowerCase() === 'approved';
};

const isHearingCompleted = (summary: CourseHearingSummary) => {
  return summary.latestStatus.toLowerCase() === 'approved';
};

export const getCurrentStudentCourseStep = ({
  userCourse,
  selfAssessSummary,
  examSummary,
  projectSummary,
  hearingSummary,
}: GetCurrentStudentCourseStepParams): CourseStep | null => {
  if (!userCourse.started && !userCourse.passed) return null;

  if (!isSelfAssessCompleted(selfAssessSummary)) {
    return 'self';
  }

  if (!isExamCompleted(examSummary)) {
    return 'exam';
  }

  if (!isProjectCompleted(projectSummary)) {
    return 'project';
  }

  if (!isHearingCompleted(hearingSummary)) {
    return 'hearing';
  }

  return 'hearing';
};

export const getCourseStepLabel = (step: CourseStep | null, coursePassed?: boolean) => {
  if (coursePassed) return 'Completed';

  if (step === 'self') return 'Self-assess';
  if (step === 'exam') return 'Exam';
  if (step === 'project') return 'Project';
  if (step === 'hearing') return 'Hearing';

  return 'Not started';
};
